import styled from "styled-components";
import { PageTitle } from "../modules/PageHeaders";
import { useState, useEffect } from "react";
import { PageTab } from "../../../components";
import NewMessage from "./modules/NewMessage";
import { AnimatePresence } from "framer-motion";
import {
  useCreateMessageMutation,
  useUpdateDraftMessageMutation,
  useSendDraftedMessageMutation,
  useGetMessageDetailQuery,
} from "../../../store/admin/communicationSlice";
import { toast } from "react-hot-toast";
import toastNotify from "../modules/toastNotify";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import DraftedMessages from "./modules/DraftedMessages";
import SentMessages from "./modules/SentMessages";
import { useGetCommunicationMessageRecipientTypesQuery } from "../../../store/selectableSlice";

const initialValues = {
  group_id: "",
  receipt_type_id: "",
  header: "",
  sending_mode: "",
  recipients: [],
};

export const Communications = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editDraftId = searchParams.get("edit");
  const [message, setMessage] = useState("");
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [editorKey, setEditorKey] = useState(0);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [tabName, setTab] = useState("Compose new message");

  const { data: receiptTypes = [] } =
    useGetCommunicationMessageRecipientTypesQuery();

  const { data: draftData, isLoading: isLoadingDraft } = useGetMessageDetailQuery(
    editDraftId,
    { skip: !editDraftId }
  );

  // Load draft data when editDraftId changes
  useEffect(() => {
    if (draftData && editDraftId) {
      const draft = draftData;
      setEditingDraftId(editDraftId);
      
      // Ensure receipt_type_id is converted to string for select compatibility
      const receiptTypeId = draft.receipt_type_id 
        ? String(draft.receipt_type_id) 
        : "";
      
      // Ensure group_id is converted to string for select compatibility
      const groupId = draft.group_id 
        ? String(draft.group_id) 
        : "";
      
      // Transform recipients from API format to form format
      // API returns: [{ id, name, ... }] or [{ member_id, ... }] or [{ member: { id, ... }, ... }]
      // Form expects: [{ member_id }]
      let transformedRecipients = [];
      if (Array.isArray(draft.recipients) && draft.recipients.length > 0) {
        transformedRecipients = draft.recipients.map((recipient) => {
          // Handle different API formats
          if (recipient.member_id != null) {
            return { member_id: recipient.member_id };
          } else if (recipient.id != null) {
            // API might return { id, name, ... } format
            return { member_id: recipient.id };
          } else if (recipient.member?.id != null) {
            // API might return nested { member: { id, ... } } format
            return { member_id: recipient.member.id };
          } else if (recipient.member?.member_id != null) {
            return { member_id: recipient.member.member_id };
          }
          return null;
        }).filter(Boolean);
      }
      
      setFormValues({
        group_id: groupId,
        receipt_type_id: receiptTypeId,
        header: draft.header || "",
        sending_mode: draft.sending_mode || "",
        recipients: transformedRecipients,
      });
      setMessage(draft.body || draft.content_email || "");
      setEditorKey((prev) => prev + 1); // Force TextEditor to remount with new content
      setTab("Compose new message"); // Switch to compose tab
      setSearchParams({}); // Clear URL params
    }
  }, [draftData, editDraftId, setSearchParams]);

  const handleEditDraft = (draftId) => {
    setSearchParams({ edit: draftId });
  };

  const updateForm = (e) => {
    const { name, value } = e.target;
    setFormErrors({});
    setFormValues((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "receipt_type_id") {
        const typeName = receiptTypes.find(
          (r) => String(r.id) === String(value)
        )?.name;
        if (typeName === "Individual") next.group_id = "";
        else next.recipients = [];
      }
      return next;
    });
  };

  const updateRecipients = (selectedRecipients = []) => {
    const list = Array.isArray(selectedRecipients) ? selectedRecipients : [];
    setFormErrors((prev) => ({ ...prev, recipients: undefined }));
    setFormValues((prev) => ({
      ...prev,
      recipients: list
        .filter((r) => r && r.value != null)
        .map((r) => ({ member_id: r.value })),
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formValues.header?.trim()) {
      errors.header = ["Message title is required"];
    }
    if (!formValues.receipt_type_id) {
      errors.receipt_type_id = ["Recipient type is required"];
    }
    const receipt_type_name =
      receiptTypes.find(
        (r) => String(r.id) === String(formValues.receipt_type_id)
      )?.name || "";
    if (receipt_type_name === "Individual") {
      if (!formValues.recipients?.length) {
        errors.recipients = ["Select at least one member"];
      }
    } else {
      if (!formValues.group_id) {
        errors.group_id = ["Group member is required"];
      }
    }
    if (!formValues.sending_mode) {
      errors.sending_mode = ["Sending mode is required"];
    }
    if (!message?.trim()) {
      errors.body = ["Message body is required"];
    }
    return errors;
  };

  const [createMessage] = useCreateMessageMutation();
  const [updateDraftMessage] = useUpdateDraftMessageMutation();
  const [sendDraftedMessage] = useSendDraftedMessageMutation();
  const sendMessage = async (endpoint) => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Please fill in all required fields");
      return;
    }

    // If editing an existing draft, use update endpoint instead of create
    const isUpdatingDraft = editingDraftId && endpoint === "saveToDraft";
    const isSendingEditedDraft = editingDraftId && endpoint === "send";
    
    const reqBody = {
      ...formValues,
      group_id: parseInt(formValues.group_id) || formValues.group_id,
      receipt_type_id:
        parseInt(formValues.receipt_type_id) || formValues.receipt_type_id,
      body: message,
    };
    
    // Only add endpoint for create operations
    if (!isUpdatingDraft && !isSendingEditedDraft) {
      reqBody.endpoint = endpoint;
    }
    
    const receipt_type_name =
      receiptTypes.find(({ id }) => String(id) === formValues.receipt_type_id)
        ?.name || "";
    if (receipt_type_name === "Individual") {
      delete reqBody.group_id;
    } else {
      delete reqBody.recipients;
    }
    
    const toastId = toast.loading(
      isUpdatingDraft 
        ? "Updating draft..." 
        : isSendingEditedDraft 
        ? "Updating and sending draft..." 
        : endpoint === "send" 
        ? "Sending message..." 
        : "Saving draft..."
    );

    let data, error;
    if (isUpdatingDraft) {
      // Update existing draft
      const updateResult = await updateDraftMessage({
        messageId: editingDraftId,
        body: reqBody,
      });
      data = updateResult.data;
      error = updateResult.error;
    } else if (isSendingEditedDraft) {
      // Update draft first, then send it
      const updateResult = await updateDraftMessage({
        messageId: editingDraftId,
        body: reqBody,
      });
      if (updateResult.error || updateResult.data?.status === false) {
        data = updateResult.data;
        error = updateResult.error;
      } else {
        // Draft updated successfully, now send it
        const sendResult = await sendDraftedMessage(editingDraftId);
        data = sendResult.data;
        error = sendResult.error;
      }
    } else {
      // Create new message or send
      const createResult = await createMessage(reqBody);
      data = createResult.data;
      error = createResult.error;
    }

    toastNotify(error, data, toastId, navigate);
    
    // Reset form on successful send (not on draft save)
    if (!error && data?.status !== false && endpoint === "send") {
      setFormValues(initialValues);
      setMessage("");
      setFormErrors({});
      setEditorKey((prev) => prev + 1); // Force TextEditor to remount
      setEditingDraftId(null); // Clear editing state
    } else if (!error && data?.status !== false && isUpdatingDraft) {
      // Clear editing state after successful update
      setEditingDraftId(null);
      toast.success("Draft updated successfully");
    }
  };

  // Reset form when switching to compose tab (if not editing)
  const handleTabChange = (tab) => {
    if (tab === "Compose new message" && !editingDraftId && !editDraftId) {
      setFormValues(initialValues);
      setMessage("");
      setFormErrors({});
      setEditorKey((prev) => prev + 1);
    }
    setTab(tab);
  };

  const tabs = [
    {
      name: "Compose new message",
      component: (
        <NewMessage
          message={message}
          setMessage={setMessage}
          formValues={formValues}
          formErrors={formErrors}
          editorKey={editorKey}
          editingDraftId={editingDraftId}
          sendMessage={sendMessage}
          updateForm={updateForm}
          updateRecipients={updateRecipients}
        />
      ),
    },
    {
      name: "Draft/outbox",
      component: <DraftedMessages onEditDraft={handleEditDraft} />,
    },
    {
      name: "Sent items",
      component: <SentMessages />,
    },
  ];
  
  return (
    <>
      <Wrapper className="mx-2 mx-lg-4">
        <PageTitle title={"Communication"} />
        <div className="d-flex flex-wrap gap-3 mb-2 px-1">
          <Link
            to="/super_admin/communication/group_management"
            className="small fw-semibold text-decoration-none"
            style={{ color: "var(--color-secondary)" }}
            data-tour="staff-comm-groups"
          >
            Group management
          </Link>
          <Link
            to="/super_admin/communication/summary"
            className="small fw-semibold text-decoration-none"
            style={{ color: "var(--color-secondary)" }}
            data-tour="staff-comm-summary"
          >
            Summary
          </Link>
        </div>
        <div className="tab-header" data-tour="staff-comm-compose">
          {tabs.map(({ name }) => {
            return (
              <p
                key={name}
                className={`tab-name ${
                  tabName === name ? "active-tab" : ""
                } mb-0`}
                onClick={() => handleTabChange(name)}
              >
                {name}
              </p>
            );
          })}
        </div>
        <AnimatePresence exitBeforeEnter>
          <PageTab page={tabName} PageArray={tabs} />
        </AnimatePresence>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.section`
  .tab-header {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 2rem 2rem 1rem 2rem;
    background-color: #ffffff;
    border-radius: 10px;
  }
  .tab-name {
    padding: 0 1rem;
    color: #858585;
    font-weight: 400;
    cursor: pointer;
    transition: all 300ms linear;
  }
  .active-tab {
    color: var(--color-primary);
    border-bottom: 2px solid var(--color-primary);
    font-weight: 700;
  }
`;

export default Communications;
